import React, { useCallback, useEffect, useState } from 'react'
import { Radio } from 'atoms/Radio'
import translate from 'core/translate'
import { cn } from 'ethcss'
import styles from './deviceSettings__p2pDownload.jcss'
import { Typography } from 'atoms/Typography'
import Dropdown from '../../../blocks.simple/dropdown/dropdown'
import { Switch } from '../../../atoms/Switch'
import Button from '../../../blocks.simple/button/button'
import Checkbox from '../../../blocks.simple/checkbox/checkbox'
import Icon from 'blocks.simple/icon/icon'
import { api } from '../../../core/api/ConnectionManager'

type P2PEngagedEntity = 'broadcastSource' | 'advSource' | 'distribution'
type P2PStorageMode = 'downloadAndSendAll' | 'sendOnlyExistingFiles'

const P2P_ENGAGED_ENTITIES: P2PEngagedEntity[] = ['broadcastSource', 'advSource', 'distribution']
const P2P_WIKI_URL =
    'https://wiki.smartplayer.org/index.php?title=%D0%A0%D0%B0%D0%B1%D0%BE%D1%82%D0%B0_peer-to-peer_(P2P)_%D0%BD%D0%B0_Android'

const ENTITY_LABELS: Record<P2PEngagedEntity, string> = {
    broadcastSource: 'p2pEntityBroadcastSource',
    advSource: 'p2pEntityAdvSource',
    distribution: 'p2pEntityDistribution',
}

const normalizeEngagedEntities = (entities?: string[]): P2PEngagedEntity[] => {
    if (!Array.isArray(entities) || !entities.length) {
        return [...P2P_ENGAGED_ENTITIES]
    }

    return P2P_ENGAGED_ENTITIES.filter((entity) => entities.includes(entity))
}

export const DeviceSettingsP2PDownload = (p_: {
    enabled: boolean
    className: string | object
    group?: any
    data: any
    mode?: 'group' | 'device'
    onChange: (value: any, field: string) => void
}) => {
    const mode = p_.mode || 'group'
    const isGroup = mode === 'group'
    const [displays, setDisplays] = useState<{ id: number; name: string }[]>([])
    const [hasNestedFolders, setHasNestedFolders] = useState(false)
    const engagedEntities = normalizeEngagedEntities(p_.data?.engagedEntities)
    const storageMode: P2PStorageMode = p_.data?.storageMode || 'downloadAndSendAll'
    const masterSelectLocked = isGroup && hasNestedFolders

    const onChangeData = useCallback(
        (field: string, value: any) => {
            const nextData = {
                ...p_.data,
                engagedEntities: normalizeEngagedEntities(p_.data?.engagedEntities),
                storageMode: p_.data?.storageMode || 'downloadAndSendAll',
                [field]: value,
            }

            p_.onChange(nextData, 'p2pDownload')
        },
        [p_.data, p_.onChange]
    )

    useEffect(() => {
        if (!p_.enabled || !isGroup || !p_.group?.id) {
            return
        }

        api.send('getDisplaysFromGroups', {
            groupId: [p_.group.id],
            fields: ['id', 'name', 'status'],
        }).then((groupDisplays: any) => {
            setDisplays(groupDisplays)
        })

        api.send<{ id: number }, boolean>('getGroupHasSubgroups', { id: p_.group.id }).then((hasSubgroups) => {
            const nested = Boolean(hasSubgroups)
            setHasNestedFolders(nested)

            if (nested && p_.data?.masterSelect !== 'auto') {
                onChangeData('masterSelect', 'auto')
            }
        })
    }, [])

    if (!p_.enabled) {
        return null
    }

    const toggleEngagedEntity = (entity: P2PEngagedEntity, checked: boolean) => {
        const nextEntities = checked
            ? P2P_ENGAGED_ENTITIES.filter((item) => engagedEntities.includes(item) || item === entity)
            : engagedEntities.filter((item) => item !== entity)

        onChangeData('engagedEntities', nextEntities)
    }

    const randomizeMaster = () => {
        const candidates = displays.filter((display) => display.id !== p_.data.masterDeviceId)
        const pool = candidates.length ? candidates : displays

        if (!pool.length) {
            return
        }

        const display = pool[Math.floor(Math.random() * pool.length)]
        onChangeData('masterDeviceId', display.id)
    }

    return (
        <div className={cn(p_.className, styles.wrapper)}>
            <div className={styles.switchWrapper}>
                <Typography className={cn(styles.title)} type="title">
                    {translate('p2pDownloadMode')}
                </Typography>
                <Switch
                    onChange={() => {
                        onChangeData('enabled', !p_.data.enabled)
                    }}
                    checked={Boolean(p_.data.enabled)}
                    className={styles.switch}
                />
            </div>
            <div className={!p_.data.enabled ? styles.disabled : ''}>
                {isGroup && (
                    <>
                        <Typography className={cn(styles.title)} type="title">
                            {translate('useHttps')}
                        </Typography>
                        <div className={styles.radioWrapper}>
                            <Radio
                                className={styles.radio}
                                onClick={function () {
                                    onChangeData('enableTls', true)
                                }}
                                selected={p_.data.enableTls}
                                label={translate('yes')}
                            />
                            <Radio
                                onClick={function () {
                                    onChangeData('enableTls', false)
                                }}
                                selected={!p_.data.enableTls}
                                label={translate('no')}
                            />
                        </div>
                        <Typography className={cn(styles.title)} type="title">
                            {translate('masterSelection')}
                        </Typography>
                        <div className={styles.radioWrapper}>
                            <Radio
                                className={styles.radio}
                                onClick={function () {
                                    onChangeData('masterSelect', 'auto')
                                }}
                                selected={p_.data.masterSelect === 'auto' || masterSelectLocked}
                                label={translate('auto')}
                            />
                            <Radio
                                onClick={function () {
                                    if (masterSelectLocked) {
                                        return
                                    }
                                    onChangeData('masterSelect', 'manual')
                                }}
                                selected={p_.data.masterSelect === 'manual' && !masterSelectLocked}
                                label={translate('manual')}
                                disabled={masterSelectLocked}
                            />
                        </div>
                        {masterSelectLocked && (
                            <Typography className={styles.hint} type="text">
                                {translate('p2pNestedFoldersStorageHint')}
                            </Typography>
                        )}
                        {p_.data.masterSelect === 'manual' && !masterSelectLocked && (
                            <>
                                <div className={styles.masterGroupTitle}>
                                    <Typography className={cn(styles.title)} type="title">
                                        {translate('selectMasterFromGroup')}
                                    </Typography>
                                </div>
                                <div>
                                    <Dropdown
                                        label={translate('deviceModels')}
                                        containerClassName={styles.codeInput}
                                        value={p_.data.masterDeviceId}
                                        options={displays}
                                        onChange={(selected) => {
                                            onChangeData('masterDeviceId', selected.id)
                                        }}
                                    />
                                </div>
                                <div className={styles.masterAutoSelectWrapper}>
                                    <div>{translate('setMasterAutomatically')}</div>
                                    <Button mod="fill" className={styles.button} onClick={randomizeMaster}>
                                        {translate('reassign')}
                                    </Button>
                                </div>
                            </>
                        )}
                    </>
                )}

                <Typography className={cn(styles.title)} type="title">
                    {translate('p2pEngagedEntities')}
                </Typography>
                <div className={styles.checkboxSection}>
                    {P2P_ENGAGED_ENTITIES.map((entity) => (
                        <Checkbox
                            key={entity}
                            label={translate(ENTITY_LABELS[entity])}
                            checked={engagedEntities.includes(entity)}
                            className={styles.checkboxWrapper}
                            onClick={(checked: boolean) => toggleEngagedEntity(entity, checked)}
                            labelClassName={styles.labelClassName}
                        />
                    ))}
                </div>

                {isGroup && (
                    <>
                        <Typography className={cn(styles.title, styles.titleWithLink)} type="title">
                            {translate('p2pStorageMode')}
                            <a
                                className={styles.wikiLink}
                                href={P2P_WIKI_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={translate('documentation')}
                            >
                                <Icon type="attention" size={16} />
                            </a>
                        </Typography>
                        <div className={styles.radioColumn}>
                            <Radio
                                className={styles.radioBlock}
                                onClick={function () {
                                    onChangeData('storageMode', 'downloadAndSendAll')
                                }}
                                selected={storageMode === 'downloadAndSendAll'}
                                label={translate('p2pStorageModeDownloadAndSendAll')}
                            />
                            <Radio
                                className={styles.radioBlock}
                                onClick={function () {
                                    onChangeData('storageMode', 'sendOnlyExistingFiles')
                                }}
                                selected={storageMode === 'sendOnlyExistingFiles'}
                                label={translate('p2pStorageModeSendOnlyExisting')}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
